import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Typography,
  Divider,
} from '@mui/material'
import { Language as LanguageIcon } from '@mui/icons-material'
import { useAuth } from '../../contexts/AuthContext'
import { UserService } from '../../services/userService'
import { SUPPORTED_LANGUAGES } from '../../i18n'

interface ProfileEditModalProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  userId: string
  currentProfile: {
    name: string
    email: string
    department: string | null
    position: string | null
    role: string
  }
}

interface FormData {
  name: string
  email: string
  department: string
  position: string
  role: string
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

const ProfileEditModal: React.FC<ProfileEditModalProps> = ({
  open,
  onClose,
  onSuccess,
  userId,
  currentProfile,
}) => {
  const { t, i18n } = useTranslation('profile')
  const { user, updateLanguage } = useAuth()
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    department: '',
    position: '',
    role: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [changePassword, setChangePassword] = useState(false)

  useEffect(() => {
    if (open && currentProfile) {
      setFormData({
        name: currentProfile.name,
        email: currentProfile.email,
        department: currentProfile.department || '',
        position: currentProfile.position || '',
        role: currentProfile.role,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      })
      setError(null)
      setSuccess(null)
      setChangePassword(false)
    }
  }, [open, currentProfile])

  const handleInputChange =
    (field: keyof FormData) =>
    (
      event:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | { target: { value: string } }
    ) => {
      setFormData((prev) => ({
        ...prev,
        [field]: event.target.value,
      }))
    }

  const validateForm = (): boolean => {
    if (!formData.name.trim() || !formData.email.trim()) {
      setError(t('editModal.validation.nameEmailRequired'))
      return false
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      setError(t('editModal.validation.invalidEmail'))
      return false
    }

    if (changePassword) {
      if (!formData.currentPassword) {
        setError(t('editModal.validation.currentPasswordRequired'))
        return false
      }

      if (!formData.newPassword || formData.newPassword.length < 6) {
        setError(t('editModal.validation.newPasswordMinLength'))
        return false
      }

      if (formData.newPassword !== formData.confirmPassword) {
        setError(t('editModal.validation.passwordMismatch'))
        return false
      }
    }

    return true
  }

  const handleSave = async () => {
    if (!validateForm()) return

    try {
      setLoading(true)
      setError(null)

      // プロフィール更新用のデータを準備
      const updateData: any = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        department: formData.department.trim() || null,
        position: formData.position.trim() || null,
      }

      // 自分以外のユーザーの役職変更は管理者のみ可能
      if (userId !== user?.id && user?.role === 'SUPER_ADMIN') {
        updateData.role = formData.role
      }

      // パスワード変更が必要な場合
      if (changePassword) {
        updateData.currentPassword = formData.currentPassword
        updateData.newPassword = formData.newPassword
      }

      await UserService.updateProfile(userId, updateData)

      setSuccess(t('editModal.success'))
      setTimeout(() => {
        onSuccess()
        onClose()
      }, 1500)
    } catch (error: any) {
      console.error('Profile update error:', error)
      setError(error.response?.data?.error || t('editModal.error'))
    } finally {
      setLoading(false)
    }
  }

  const canEditRole = user?.role === 'SUPER_ADMIN' && userId !== user?.id
  const isOwnProfile = userId === user?.id

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('editModal.title')}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Stack spacing={3}>
            {/* 基本情報 */}
            <Box>
              <Typography variant="h6" gutterBottom>
                {t('editModal.basicInfoSection')}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Stack spacing={2}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      label={t('basicInfo.name')}
                      value={formData.name}
                      onChange={handleInputChange('name')}
                      required
                      disabled={loading}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      label={t('basicInfo.email')}
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange('email')}
                      required
                      disabled={loading}
                    />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      label={t('basicInfo.department')}
                      value={formData.department}
                      onChange={handleInputChange('department')}
                      disabled={loading}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      label={t('basicInfo.position')}
                      value={formData.position}
                      onChange={handleInputChange('position')}
                      disabled={loading}
                    />
                  </Box>
                </Box>

                {/* 言語設定（自分のプロフィールのみ） */}
                {isOwnProfile && (
                  <FormControl fullWidth disabled={loading}>
                    <InputLabel>{t('basicInfo.language')}</InputLabel>
                    <Select
                      value={i18n.language}
                      label={t('basicInfo.language')}
                      onChange={async (e) => {
                        const newLang = e.target.value as 'ja' | 'en'
                        const backendLang = newLang === 'en' ? 'EN' : 'JA'
                        try {
                          await updateLanguage(backendLang)
                        } catch (error) {
                          console.error('Failed to save language setting:', error)
                          await i18n.changeLanguage(newLang)
                        }
                      }}
                      startAdornment={<LanguageIcon sx={{ mr: 1, color: 'action.active' }} />}
                    >
                      {SUPPORTED_LANGUAGES.map((lang) => (
                        <MenuItem key={lang.code} value={lang.code}>
                          {lang.nativeLabel} ({lang.label})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {/* 権限（管理者のみ編集可能、自分以外） */}
                {canEditRole && (
                  <FormControl fullWidth disabled={loading}>
                    <InputLabel>{t('basicInfo.role')}</InputLabel>
                    <Select
                      value={formData.role}
                      label={t('basicInfo.role')}
                      onChange={handleInputChange('role')}
                    >
                      <MenuItem value="USER">{t('roles.USER')}</MenuItem>
                      <MenuItem value="CORPORATE_ADMIN">{t('roles.CORPORATE_ADMIN')}</MenuItem>
                      <MenuItem value="SUPER_ADMIN">{t('roles.SUPER_ADMIN')}</MenuItem>
                    </Select>
                  </FormControl>
                )}
              </Stack>
            </Box>

            {/* パスワード変更（自分のプロフィールのみ） */}
            {isOwnProfile && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {t('editModal.passwordSection')}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Button
                  variant={changePassword ? 'outlined' : 'text'}
                  onClick={() => setChangePassword(!changePassword)}
                  disabled={loading}
                >
                  {changePassword
                    ? t('editModal.cancelPasswordChange')
                    : t('editModal.changePassword')}
                </Button>

                {changePassword && (
                  <Stack spacing={2} sx={{ mt: 2 }}>
                    <TextField
                      fullWidth
                      label={t('editModal.currentPassword')}
                      type="password"
                      value={formData.currentPassword}
                      onChange={handleInputChange('currentPassword')}
                      required
                      disabled={loading}
                    />

                    <Box
                      sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <TextField
                          fullWidth
                          label={t('editModal.newPassword')}
                          type="password"
                          value={formData.newPassword}
                          onChange={handleInputChange('newPassword')}
                          required
                          disabled={loading}
                          helperText={t('editModal.passwordHelperText')}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <TextField
                          fullWidth
                          label={t('editModal.confirmPassword')}
                          type="password"
                          value={formData.confirmPassword}
                          onChange={handleInputChange('confirmPassword')}
                          required
                          disabled={loading}
                        />
                      </Box>
                    </Box>
                  </Stack>
                )}
              </Box>
            )}
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t('editModal.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? t('editModal.saving') : t('editModal.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ProfileEditModal
